import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

// All skills from resume - each with a unique color
const allSkills = [
  { name: 'Python', level: 95, color: '#4B8BBE' },
  { name: 'Machine Learning', level: 90, color: '#38B2AC' },
  { name: 'Deep Learning (PyTorch/TensorFlow)', level: 90, color: '#FF6B6B' },
  { name: 'LLMs & Generative AI', level: 85, color: '#61DAFB' },
  { name: 'SQL', level: 85, color: '#F29111' },
  { name: 'Data Engineering (PySpark, Snowflake)', level: 80, color: '#4299E1' },
  { name: 'FastAPI/Flask', level: 85, color: '#805AD5' },
  { name: 'Docker & Kubernetes', level: 80, color: '#2496ED' },
  { name: 'PostgreSQL', level: 85, color: '#336791' },
  { name: 'Redis', level: 80, color: '#DC382D' },
  { name: 'Microservices', level: 80, color: '#9B59B6' },
  { name: 'OOP', level: 90, color: '#E74C3C' },
  { name: 'Distributed Systems', level: 75, color: '#16A085' },
  { name: 'OCR', level: 80, color: '#F39C12' },
  { name: 'AWS', level: 85, color: '#FF9900' },
  { name: 'Prompt Engineering', level: 85, color: '#8E44AD' },
  { name: 'PyTorch', level: 90, color: '#EE4C2C' },
  { name: 'RoBERTa', level: 85, color: '#E67E22' },
  { name: 'CNN-LSTM', level: 85, color: '#1ABC9C' },
  { name: 'Transformers', level: 85, color: '#3498DB' },
  { name: 'NLP', level: 90, color: '#2ECC71' },
  { name: 'Computer Vision', level: 85, color: '#E91E63' },
  { name: 'Multimodal AI', level: 85, color: '#9C27B0' },
  { name: 'GPT', level: 85, color: '#10A37F' },
  { name: 'AssemblyAI', level: 80, color: '#00BCD4' },
  { name: 'RAG', level: 85, color: '#3F51B5' },
  { name: 'Pinecone', level: 80, color: '#009688' },
  { name: 'Mask R-CNN', level: 85, color: '#FF5722' },
  { name: 'LLaMA-2', level: 85, color: '#673AB7' },
  { name: 'Medical Imaging', level: 80, color: '#00ACC1' },
  { name: 'BERTweet', level: 85, color: '#4CAF50' },
  { name: 'DeepFace', level: 80, color: '#FF1744' },
  { name: 'Pytesseract', level: 75, color: '#2196F3' },
  { name: 'NLTK', level: 85, color: '#FF9800' },
  { name: 'Web Scraping', level: 80, color: '#00E676' },
  { name: 'TensorFlow', level: 85, color: '#FF6F00' },
  { name: 'PySpark', level: 80, color: '#E25A1C' },
  { name: 'Snowflake', level: 75, color: '#29B5E8' },
];

// Function to randomly select 8 skills from all skills
const getRandomSkills = (count: number = 8) => {
  const shuffled = [...allSkills].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, count);
};

const About: React.FC = () => {
  const [displayedSkills, setDisplayedSkills] = useState(() => getRandomSkills(8));

  useEffect(() => {
    const interval = setInterval(() => {
      setDisplayedSkills(getRandomSkills(8));
    }, 15000); // Change every 15 seconds

    return () => clearInterval(interval);
  }, []);

  return (
    <section id="about" className="py-20 relative overflow-hidden">
      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-purple-500">About Me</span>
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto rounded-full"></div>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-12 items-start">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200">
              <h3 className="text-2xl font-semibold text-gray-800 mb-6">Who I Am</h3>
              <p className="text-gray-600 mb-4">
                I'm a Data Science Master's student at NYU with a passion for building scalable AI/ML systems. My experience spans from optimizing backend systems with microservices and Redis caching to engineering OCR parsers and deploying ML pipelines on AWS. I specialize in LLM fine-tuning, RAG systems, and multimodal AI models.
              </p>
              <p className="text-gray-600 leading-relaxed">
                I've worked on diverse projects from building multimodal sarcasm detection models to developing AI meeting summarizers and medical image segmentation systems. My research has been published in IEEE, CRC, and Elsevier journals. I'm passionate about turning complex data into actionable insights and deploying production-ready AI solutions.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="bg-white rounded-xl p-4 shadow-lg border border-gray-200"
              >
                <div className="text-3xl font-bold text-primary-color mb-2">1+</div>
                <div className="text-gray-600">Years Experience</div>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="bg-white rounded-xl p-4 shadow-lg border border-gray-200"
              >
                <div className="text-3xl font-bold text-primary-color mb-2">4</div>
                <div className="text-gray-600">Research Publications</div>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="bg-white rounded-xl p-4 shadow-lg border border-gray-200"
              >
                <div className="text-3xl font-bold text-primary-color mb-2">10+</div>
                <div className="text-gray-600">Interactive Projects</div>
              </motion.div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200">
              <h3 className="text-2xl font-semibold text-gray-800 mb-6">Skills</h3>
              <div className="space-y-4">
                {displayedSkills.map((skill, index) => (
                  <motion.div
                    key={`${skill.name}-${index}`}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <div className="flex justify-between mb-1">
                      <span className="text-gray-700 font-medium">{skill.name}</span>
                      <span className="text-gray-500">{skill.level}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <motion.div 
                        className="h-2 rounded-full"
                        style={{ backgroundColor: skill.color }}
                        initial={{ width: 0 }}
                        animate={{ width: `${skill.level}%` }}
                        transition={{ duration: 0.8, delay: index * 0.1 }}
                      />
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200">
              <h3 className="text-2xl font-semibold text-gray-800 mb-4">Education</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full bg-primary-color/10 flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-primary-color" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14l9-5-9-5-9 5 9 5z"/>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z"/>
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">Master of Science, Data Science</h4>
                    <p className="text-gray-600">New York University</p>
                    <p className="text-sm text-gray-500">Aug 2025 – May 2027</p>
                    <p className="text-xs text-gray-500 mt-1">Optimized Linear Algebra, Applied Machine Learning, Big Data Analytics, Reinforcement Learning</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full bg-primary-color/10 flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-primary-color" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14l9-5-9-5-9 5 9 5z"/>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z"/>
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">Bachelor of Technology, Computer Engineering</h4>
                    <p className="text-gray-600">Pandit Deendayal Energy University</p>
                    <p className="text-sm text-gray-500">Sept 2021 – May 2025 | GPA: 3.7/4.0</p>
                    <p className="text-xs text-gray-500 mt-1">Machine Learning, Probability & Statistics, Algorithms, Database Management Systems, Computer Vision</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About; 